import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GlobalDeleteModal from "../../components/partials/GlobalDeleteModal";
import supplierApi from "../../api/inventory/supplierApi";
import SortableRow from "./SortableRow";
import TableShimmer from "../../components/partials/TableShimmer";
import useQueryFilters from "../../hooks/useQueryFilters";
import useDeleteItem from "../../hooks/useDeleteItem";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";

function List() {
  const [tableLoading, setTableLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});

  /* =============  FETCH API DATA ================ */
  const { query, updateFilter, handleSearch, searchParams, searchMessage } =
    useQueryFilters();

  const fetchSupplierData = useCallback(async () => {
    setTableLoading(true);

    try {
      const res = await supplierApi.getDetails(searchParams);

      if (res.code === 200) {
        const result = res.data;

        setItems(result.suppliers);
        setPagination(result.pagination);
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSupplierData();
  }, [fetchSupplierData]);

  const { isDeleting, openDelete, handleDelete } = useDeleteItem(
    supplierApi.deleteItem,
    fetchSupplierData,
  );
  /* =============  FETCH API DATA END ================ */

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Manage Suppliers</h4>
              <h6>
                View, edit, and organize supplier information displayed across
                your store.
              </h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/suppliers/add-new" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Supplier
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search By Store Name/Email Address/Phone Number"
                    defaultValue={query.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>

                {searchMessage && (
                  <small className="text-danger">{searchMessage}</small>
                )}
              </div>
              <div className="col-md-6" style={{ justifyItems: "right" }}>
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center row-gap-3">
                  <select
                    className="form-control ms-2"
                    value={searchParams.get("status") || ""}
                    onChange={(e) => updateFilter("status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-12">
                <div className="countablepagination p-2">
                  <p>
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}{" "}
                    of {pagination.total} results
                  </p>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table w-100">
                <thead className="thead-light">
                  <tr>
                    <th>S. No.</th>
                    <th>Supplier Details</th>
                    <th>Contact Person</th>
                    <th>Contact Details</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={5} />
                  ) : items.length > 0 ? (
                    <>
                      {items.map((item, index) => (
                        <SortableRow
                          key={item._id}
                          item={item}
                          index={index}
                          page={pagination?.page}
                          limit={pagination?.limit}
                          openDelete={openDelete}
                        />
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center p-4 text-muted">
                        No Supplier Added,{" "}
                        <Link to="/suppliers/add-new" className="text-primary">
                          Click Here{" "}
                        </Link>
                        to add new supplier
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default List;
